/**
 * whatsappApi — thin wrapper over the WhatsApp Phase A read endpoint.
 *
 * Provider-blind on purpose: the frontend only knows normalized message
 * rows (direction, origin, message_type, text, timestamps). Anything
 * Cosend/Meta-specific stays backend-side. Errors re-throw so callers
 * render `formatError(e)` with the existing pattern.
 */
import { api } from "@/lib/api";

export async function getLeadWhatsAppMessages(leadId, { limit, before } = {}) {
  const params = {};
  if (limit) params.limit = limit;
  if (before) params.before = before;
  const { data } = await api.get(
    `/leads/${encodeURIComponent(leadId)}/whatsapp/messages`,
    { params },
  );
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    nextBefore: data?.next_before || null,
  };
}

export async function sendLeadWhatsAppMessage(leadId, { text, clientRequestId }) {
  const { data } = await api.post(
    `/leads/${encodeURIComponent(leadId)}/whatsapp/messages`,
    { text, client_request_id: clientRequestId },
  );
  return data;
}

export async function markLeadWhatsAppRead(leadId) {
  const { data } = await api.post(
    `/leads/${encodeURIComponent(leadId)}/whatsapp/read`,
  );
  return data;
}

export async function retryLeadWhatsAppMedia(leadId, messageId) {
  const { data } = await api.post(
    `/leads/${encodeURIComponent(leadId)}/whatsapp/messages/${encodeURIComponent(messageId)}/media/retry`,
  );
  return data;
}

/** Meta-approved WhatsApp templates (Phase E) — the ONLY sendable
 * content when the 24h window is closed. Distinct from the CRM
 * suggested-messages catalog (messageTemplatesApi). */
export async function getWhatsAppMetaTemplates() {
  const { data } = await api.get(`/whatsapp/meta-templates`);
  return Array.isArray(data?.templates) ? data.templates : [];
}

export async function sendLeadWhatsAppTemplate(
  leadId, { templateName, language, variables, clientRequestId },
) {
  const { data } = await api.post(
    `/leads/${encodeURIComponent(leadId)}/whatsapp/template-messages`,
    {
      template_name: templateName,
      language: language || null,
      variables: variables || [],
      client_request_id: clientRequestId,
    },
  );
  return data;
}

/** AI Reply Copilot (Fase 3C): borrador IA para el composer. NUNCA envía —
 * el texto vuelve al textarea y el envío sigue siendo el pipeline normal.
 * Misma autorización que la conversación (admin-only V1). */
export async function generateLeadAiReply(leadId, { mode = "reply", regenerate = false } = {}) {
  const { data } = await api.post(
    `/leads/${encodeURIComponent(leadId)}/ai-reply`,
    { mode, regenerate },
  );
  return data;
}

/** Admin recovery (Fase 1.2): envíos sin resolver y su resolución
 * manual auditada. Solo admin; jamás llama al provider. */
export async function getWhatsAppPendingSends() {
  const { data } = await api.get(`/admin/whatsapp/pending-sends`);
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    count: data?.count || 0,
  };
}

export async function markWhatsAppPendingFailed(sendRequestId, { reason, externalErrorCode }) {
  const { data } = await api.post(
    `/admin/whatsapp/pending-sends/${encodeURIComponent(sendRequestId)}/mark-failed`,
    { reason, external_error_code: externalErrorCode || null },
  );
  return data;
}

export async function markWhatsAppPendingSent(sendRequestId, { providerMessageId, reason }) {
  const { data } = await api.post(
    `/admin/whatsapp/pending-sends/${encodeURIComponent(sendRequestId)}/mark-sent`,
    { provider_message_id: providerMessageId, reason },
  );
  return data;
}
